import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminPartnersService } from "@/services/admin-partners-service";
import {
  AdminPartnerProviderConfiguration,
  SaveAdminPartnerProviderRequest,
} from "@/types/admin-partner-provider";
import {
  PartnerHotelActivityLimit,
  UpdatePartnerHotelActivityLimitRequest,
} from "@/types/partner-settings";

export const partnerProviderKeys = {
  all: ["partnerProviders"] as const,
  lists: () => [...partnerProviderKeys.all, "list"] as const,

  hotelActivityLimit: (hotelId: string, tenantId: string) =>
    [
      ...partnerProviderKeys.all,
      "hotelActivityLimit",
      hotelId,
      tenantId,
    ] as const,
};

export const usePartnerProviders = () => {
  return useQuery<AdminPartnerProviderConfiguration[]>({
    queryKey: partnerProviderKeys.lists(),
    queryFn: () => adminPartnersService.listProviders(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useSavePartnerProvider = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AdminPartnerProviderConfiguration,
    unknown,
    SaveAdminPartnerProviderRequest
  >({
    mutationFn: async ({ provider, enabled, data }) => {
      const savedProvider = await adminPartnersService.upsertProvider(
        provider,
        data,
      );

      if (savedProvider.enabled !== enabled) {
        if (enabled) {
          await adminPartnersService.enableProvider(provider);
        } else {
          await adminPartnersService.disableProvider(provider);
        }
      }

      return {
        ...savedProvider,
        enabled,
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: partnerProviderKeys.lists(),
      });
    },
  });
};

/**
 * Per-hotel activity-limit override — admin-only, not wired into any
 * hotel-manager-facing screen. `GET/PUT /admin/hotels/:hotelId/partners/limit`
 * (contract 2026-09-27, backend dev@52c7061) needs the hotel's tenantId.
 */
export const useHotelActivityLimit = (
  hotelId: string | null,
  tenantId: string | null,
) => {
  return useQuery<PartnerHotelActivityLimit>({
    queryKey: partnerProviderKeys.hotelActivityLimit(
      hotelId || "",
      tenantId || "",
    ),
    enabled: !!hotelId && !!tenantId,
    queryFn: () =>
      adminPartnersService.getHotelActivityLimit(hotelId!, tenantId!),
  });
};

export const useUpdateHotelActivityLimit = (
  hotelId: string | null,
  tenantId: string | null,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    PartnerHotelActivityLimit,
    unknown,
    UpdatePartnerHotelActivityLimitRequest
  >({
    mutationFn: async (data) => {
      if (!hotelId || !tenantId) {
        throw new Error("hotelId and tenantId are required");
      }
      return adminPartnersService.updateHotelActivityLimit(
        hotelId,
        tenantId,
        data,
      );
    },
    onSuccess: () => {
      if (!hotelId || !tenantId) return;
      queryClient.invalidateQueries({
        queryKey: partnerProviderKeys.hotelActivityLimit(hotelId, tenantId),
      });
    },
  });
};
